import { useTranslation } from "react-i18next";
import { ShopHeroReveal } from "./ShopHeroReveal";
import { ShopHeroTitle } from "./ShopHeroTitle";

export interface ShopHeroCopyProps {
  animate: boolean;
}

/**
 * Lo que dice la portada.
 *
 * ── El texto viene de la landing, no de un generador ─────────────────
 * «Bienvenido al siguiente nivel.» es el `<h1>` literal de
 * `itsthebest1111.com`. «Nutrición que llega a tu célula» es el titular de
 * su segunda sección. El párrafo es el de su portada, con la tilde de
 * «línea» puesta. Ese era el encargo: que la tienda suene a la página
 * principal, no que suene bien por su cuenta. «Elige tu plan ideal» podría
 * ser de cualquier tienda de suplementos del mundo.
 *
 * ── El panel es la mitad de la legibilidad ───────────────────────────
 * `bg-ds-ink/70` no es estética: es el segundo factor de la medición que
 * documenta `ShopHeroVeil`. Velo (40 % en escritorio, 60 % en móvil) por
 * panel (70 %) da la alfa efectiva de 0.82 / 0.88 con la que el titular
 * saca 10.4 : 1 sobre el fotograma MÁS CLARO del vídeo.
 *
 * En escritorio no se ve como una caja: allí debajo ya hay tinta casi
 * sólida —el vídeo está apartado a la derecha— y una tinta al 70 % sobre
 * tinta es invisible. Solo aparece donde hace falta, que es el teléfono.
 *
 * ── La sombra existe para que el panel no tenga borde ────────────────
 * `shadow-[0_0_90px_46px_…]` en el MISMO color y la misma alfa que el
 * fondo del panel. Un rectángulo redondeado y opaco encima de un vídeo se
 * lee como una tarjeta pegada; con 46 px de expansión difuminada, el
 * panel no termina en ningún sitio concreto y se lee como una penumbra.
 *
 * ── Qué NO hay aquí ──────────────────────────────────────────────────
 * Ni cápsula de rótulo ni serif itálica: Anahi las retiró del sistema. El
 * rótulo se lee como rótulo por su tamaño, sus mayúsculas y su espaciado.
 * Tampoco hay botón: el control de la portada es la flecha de bajar, y
 * dos llamadas a la acción compitiendo a treinta píxeles de distancia se
 * anulan.
 */
export function ShopHeroCopy({ animate }: ShopHeroCopyProps) {
  const { t } = useTranslation();

  return (
    /*
     * El ancho se queda en `max-w-md` hasta `lg`. Es estrecho a
     * propósito: con la caja más ancha, en tableta cabía «Bienvenido al
     * siguiente» en la primera línea y «nivel.» se quedaba sola en la
     * segunda — el titular partido por un sitio que no es el suyo y el
     * cambio de color a media línea. Con este ancho, el corte cae entre
     * los dos segmentos en todos los tamaños por debajo de `lg`, que es
     * donde también cambia el color.
     */
    <div className="relative mx-auto max-w-md rounded-[2rem] bg-ds-ink/70 px-6 py-10 text-center shadow-[0_0_90px_46px_rgba(31,29,43,0.70)] sm:px-10 sm:py-12 lg:mx-0 lg:max-w-xl lg:text-left">
      <ShopHeroReveal animate={animate} delay={0}>
        <span className="inline-flex items-center text-ds-label font-bold uppercase tracking-ds-label-wide text-ds-lilac">
          {t("Catálogo")}
        </span>
      </ShopHeroReveal>

      <ShopHeroTitle
        segments={[{ text: t("Bienvenido al") }, { text: t("siguiente nivel."), accent: true }]}
        animate={animate}
        startDelay={120}
        step={90}
        className="mt-5 text-[34px] font-light leading-[1.05] tracking-tight text-white sm:text-ds-h1 lg:text-ds-display"
      />

      <ShopHeroReveal animate={animate} delay={560} className="mt-6">
        <p className="text-[19px] font-light leading-snug text-white sm:text-[22px]">
          {t("Nutrición que llega a tu célula.")}
        </p>
      </ShopHeroReveal>

      <ShopHeroReveal animate={animate} delay={700} className="mt-4">
        <p className="mx-auto max-w-md text-[15px] leading-relaxed text-ds-mist lg:mx-0">
          {t(
            "Más que una línea de suplementos: un estilo de vida enfocado en ti. No es solo un camino a la plenitud, sino el mejor.",
          )}
        </p>
      </ShopHeroReveal>
    </div>
  );
}
